import React, { useState, useEffect } from 'react';
import { adminPagesAPI } from '../../lib/api';
import { Plus, Pencil, Trash2, Eye, EyeOff, FileText, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link', 'image'],
    ['blockquote', 'code-block'],
    ['clean']
  ],
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'align',
  'link', 'image', 'blockquote', 'code-block'
];

const AdminPagesPage = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editPage, setEditPage] = useState(null);
  const [form, setForm] = useState({ title: '', slug: '', body: '', meta_title: '', meta_description: '', active: true });
  const [showPreview, setShowPreview] = useState(false);

  const fetchPages = async () => {
    try {
      const res = await adminPagesAPI.getAll();
      setPages(res.data.pages);
    } catch { toast.error('Failed to load pages'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPages(); }, []);

  const openCreate = () => {
    setEditPage(null);
    setForm({ title: '', slug: '', body: '', meta_title: '', meta_description: '', active: true });
    setShowEditor(true);
    setShowPreview(false);
  };

  const openEdit = (page) => {
    setEditPage(page);
    setForm({ title: page.title, slug: page.slug, body: page.body || '', meta_title: page.meta_title || '', meta_description: page.meta_description || '', active: page.active });
    setShowEditor(true);
    setShowPreview(false);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      if (editPage) {
        await adminPagesAPI.update(editPage.id, form);
        toast.success('Page updated');
      } else {
        await adminPagesAPI.create(form);
        toast.success('Page created');
      }
      setShowEditor(false);
      fetchPages();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save page'); }
  };

  const handleDelete = async (page) => {
    if (!window.confirm(`Delete "${page.title}"?`)) return;
    try {
      await adminPagesAPI.delete(page.id);
      toast.success('Page deleted');
      fetchPages();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot delete this page'); }
  };

  const handleToggle = async (page) => {
    try {
      const res = await adminPagesAPI.toggle(page.id);
      toast.success(res.data.active ? 'Page activated' : 'Page deactivated');
      fetchPages();
    } catch { toast.error('Failed to toggle page'); }
  };

  if (loading) return <div className="p-8 text-center"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" /></div>;

  if (showEditor) return (
    <div className="p-6 space-y-6" data-testid="page-editor">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">{editPage ? 'Edit Page' : 'Create Page'}</h2>
        <div className="flex gap-2">
          <button onClick={() => setShowPreview(!showPreview)} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50" data-testid="toggle-preview-btn">
            {showPreview ? 'Edit' : 'Preview'}
          </button>
          <button onClick={() => setShowEditor(false)} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="save-page-btn">Save</button>
        </div>
      </div>
      {showPreview ? (
        <div className="bg-white border rounded-xl p-8">
          <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: form.body }} />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Page Title" data-testid="page-title-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL path)</label>
              <input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="page-slug" data-testid="page-slug-input" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SEO Meta Title</label>
            <input value={form.meta_title} onChange={e => setForm({...form, meta_title: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Meta Title for SEO" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SEO Meta Description</label>
            <input value={form.meta_description} onChange={e => setForm({...form, meta_description: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Meta Description for SEO" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Body Content</label>
            <div className="bg-white border rounded-lg">
              <ReactQuill
                theme="snow"
                value={form.body}
                onChange={(val) => setForm({...form, body: val})}
                modules={quillModules}
                formats={quillFormats}
                style={{ minHeight: '300px' }}
                data-testid="page-body-editor"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="admin-pages-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Manage Pages</h1>
          <p className="text-gray-500 text-sm mt-1">Create and manage static pages (Terms, Privacy, About, etc.)</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="create-page-btn">
          <Plus className="w-4 h-4" /> Create Page
        </button>
      </div>
      <div className="bg-white rounded-xl border">
        {pages.length === 0 ? (
          <div className="p-12 text-center text-gray-500"><FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p>No pages yet. Create your first page.</p></div>
        ) : (
          <table className="w-full">
            <thead><tr className="border-b bg-gray-50 text-left text-sm text-gray-600">
              <th className="px-6 py-3">Title</th><th className="px-6 py-3">Slug</th><th className="px-6 py-3">Status</th><th className="px-6 py-3">Type</th><th className="px-6 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {pages.map(page => (
                <tr key={page.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">{page.title}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">/{page.slug}</td>
                  <td className="px-6 py-4">
                    <button onClick={() => handleToggle(page)} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${page.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`} data-testid={`toggle-page-${page.slug}`}>
                      {page.active ? <><Eye className="w-3 h-3" /> Active</> : <><EyeOff className="w-3 h-3" /> Inactive</>}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-400">{page.is_default ? 'Default' : 'Custom'}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <a href={`/page/${page.slug}`} target="_blank" rel="noreferrer" className="p-1.5 text-gray-400 hover:text-blue-600" title="View"><ExternalLink className="w-4 h-4" /></a>
                      <button onClick={() => openEdit(page)} className="p-1.5 text-gray-400 hover:text-blue-600" title="Edit" data-testid={`edit-page-${page.slug}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(page)} className="p-1.5 text-gray-400 hover:text-red-600" title="Delete" data-testid={`delete-page-${page.slug}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminPagesPage;
